import React, { useState } from 'react';
import { Send, Mail, User, MessageSquare, Calendar } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      toast({
        title: "Missing API Key",
        description: "Set REACT_APP_WEB3FORMS_ACCESS_KEY in .env to enable email submissions.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          name: formData.name,
          email: formData.email,
          message: formData.message,
          subject: `New message from ${formData.name}`,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Message Sent!",
          description: "Thank you for reaching out. I'll get back to you soon.",
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <div className="section-underline"></div>
          <p className="section-description">
            Have a project in mind or want to collaborate? Feel free to reach out!
          </p>
        </div>
        <div className="contact-wrapper">
          <div className="contact-info">
            <h3 className="contact-info-title">Let's Connect</h3>
            <p className="contact-info-text">
              I'm always interested in hearing about new projects and opportunities,
              especially in backend development, AI/ML integration, and network security.
            </p>
            <div className="contact-details">
              <div className="contact-detail-item">
                <Mail size={20} />
                <div>
                  <div className="contact-detail-label">Email</div>
                  <a href={`mailto:${personalInfo.email}`} className="contact-detail-value">
                    {personalInfo.email}
                  </a>
                </div>
              </div>
              <div className="contact-detail-item">
                <User size={20} />
                <div>
                  <div className="contact-detail-label">Phone</div>
                  <a href={`tel:${personalInfo.phone}`} className="contact-detail-value">
                    {personalInfo.phone}
                  </a>
                </div>
              </div>
              <div className="contact-detail-item">
                <Calendar size={20} />
                <div>
                  <div className="contact-detail-label">Schedule</div>
                  <a
                    href="https://calendly.com/hello_rohan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-detail-value"
                  >
                    Book on Calendly
                  </a>
                </div>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Your name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="your.email@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className="form-textarea"
                placeholder="Tell me about your project or question..."
                rows={6}
              />
            </div>
            <Button
              type="submit"
              className="btn-primary form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
              <Send className="ml-2" size={18} />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;